import {
	Autocomplete,
	Grid2,
	InputAdornment,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { CheckboxTextField } from "@/controls/CheckboxTextField";
import type {
	BreakLine,
	BreakRule,
	ShiftHours,
} from "@/shared/models/BreakRule";
import {
	type BreakRuleForm,
	WHOLE_MINUTES,
	siblingRules,
	validateLabel,
	validateOffset,
	validateDuration,
} from "../utils/breakRuleForm";
import { formatMinutes } from "../utils/minutes";

const SHIFT_OPTIONS: { hours: ShiftHours; label: string }[] = [
	{ hours: 8, label: "8 hour shifts — M / A / N" },
	{ hours: 12, label: "12 hour shifts — D / N" },
];

const shiftLabel = (hours: ShiftHours) =>
	SHIFT_OPTIONS.find((o) => o.hours === hours)?.label ?? "";

const minutesHint = (text: string) =>
	WHOLE_MINUTES.test(text) ? (
		<InputAdornment position="end">
			<Typography variant="caption" color="text.secondary">
				{formatMinutes(Number(text))}
			</Typography>
		</InputAdornment>
	) : undefined;

type Props = {
	form: UseFormReturn<BreakRuleForm>;
	lines: BreakLine[];
	rules: BreakRule[];
	editingRuleId: number | null;
	locked: boolean;
	isDelete: boolean;
	disabled: boolean;
};

export const BreakRuleFields = ({
	form,
	lines,
	rules,
	editingRuleId,
	locked,
	isDelete,
	disabled,
}: Props) => {
	const {
		control,
		watch,
		getValues,
		setValue,
		trigger,
		formState: { isSubmitted },
	} = form;
	const assetId = watch("AssetId");
	const shiftHours = watch("ShiftHours");
	const offsetText = watch("OffsetMinutes");
	const selectedLine = lines.find((l) => l.AssetId === assetId);
	const siblings = () => siblingRules(rules, getValues(), editingRuleId);
	useEffect(() => {
		if (isSubmitted && !isDelete)
			void trigger(["Label", "OffsetMinutes", "DurationMinutes"]);
	}, [assetId, shiftHours, offsetText, isSubmitted, isDelete, trigger]);
	const textFields = [
		{
			name: "Label",
			label: "Label",
			validate: (value: string) => validateLabel(value, siblings()),
		},
		{
			name: "OffsetMinutes",
			label: "Starts (minutes into shift)",
			validate: (value: string) => validateOffset(value, shiftHours),
		},
		{
			name: "DurationMinutes",
			label: "Duration (minutes)",
			validate: (value: string) =>
				validateDuration(value, getValues(), siblings()),
		},
	] as const;
	return (
		<Grid2 container spacing={2}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Controller
					name="AssetId"
					control={control}
					rules={{ required: "Line is required" }}
					render={({ field, fieldState: { error } }) => (
						<Autocomplete
							options={lines}
							getOptionLabel={(option) => option.AssetName}
							isOptionEqualToValue={(a, b) => a.AssetId === b.AssetId}
							value={lines.find((l) => l.AssetId === field.value) ?? null}
							onChange={(_, line) => {
								field.onChange(line ? line.AssetId : null);
								if (line) setValue("ShiftHours", line.DefaultShiftHours);
							}}
							disabled={locked || disabled}
							size="small"
							renderInput={(params) => (
								<TextField
									{...params}
									label="Line"
									error={!!error}
									helperText={error?.message}
								/>
							)}
						/>
					)}
				/>
			</Grid2>
			<Grid2 size={{ md: 6, xs: 12 }}>
				<Controller
					name="ShiftHours"
					control={control}
					render={({ field }) => (
						<TextField
							select
							label="Shift length"
							value={field.value}
							onChange={(e) => field.onChange(Number(e.target.value))}
							size="small"
							fullWidth
							disabled={locked || disabled}
							slotProps={{
								select: {
									renderValue: (v) => shiftLabel(v as ShiftHours),
								},
							}}
						>
							{SHIFT_OPTIONS.map((opt) => (
								<MenuItem
									key={opt.hours}
									value={opt.hours}
									sx={{ justifyContent: "space-between", gap: 2 }}
								>
									{opt.label}
									{selectedLine?.DefaultShiftHours === opt.hours && (
										<Typography
											component="span"
											variant="caption"
											sx={{
												color: "text.disabled",
												fontStyle: "italic",
											}}
										>
											line default
										</Typography>
									)}
								</MenuItem>
							))}
						</TextField>
					)}
				/>
			</Grid2>
			{textFields.map(({ name, label, validate }) => (
				<Grid2 key={name} size={{ md: 6, xs: 12 }}>
					<Controller
						name={name}
						control={control}
						rules={{ validate }}
						render={({ field, fieldState: { error } }) => (
							<TextField
								{...field}
								label={label}
								error={!!error}
								helperText={error?.message}
								fullWidth
								size="small"
								autoComplete="off"
								disabled={isDelete || disabled}
								slotProps={{
									htmlInput:
										name === "Label"
											? { maxLength: 30 }
											: { inputMode: "numeric" },
									input: {
										endAdornment:
											name === "OffsetMinutes"
												? minutesHint(field.value)
												: undefined,
									},
								}}
							/>
						)}
					/>
				</Grid2>
			))}
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Controller
					name="IsSpell"
					control={control}
					render={({ field }) => (
						<CheckboxTextField
							label="Spell — crew rotates, the line keeps running"
							checked={field.value}
							onChange={field.onChange}
							disabled={isDelete || disabled}
						/>
					)}
				/>
			</Grid2>
		</Grid2>
	);
};
